'use client';

import { useEffect, useRef } from 'react';
import { useGlobalModal, useRecreate } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo } from '@/app/types';
import { EllipsisActionTypeEnum } from '@/app/types';
import { getReportParams, reportEvent } from '@/app/utils';
import { FINISHED_STATES, FIRST_CREATION_INDEX } from '../constants';
import { useTaskState } from './use-task-state';

interface UseTaskCardOptions {
  task: TaskInfo;
  scrollToTop?: () => void;
  /** 页面本地列表更新 SSE / 详情回写，不传则写全局 taskList */
  onTaskUpdate?: (taskId: string, task: Partial<TaskInfo>) => void;
  /** 页面本地列表删除，不传则走全局 removeTask */
  onRemoveTask?: (taskId: string) => void;
}

export const useTaskCard = ({ task, scrollToTop, onTaskUpdate, onRemoveTask }: UseTaskCardOptions) => {
  const { removeTask: storeRemoveTask } = useStore((state) => ({
    removeTask: state.removeTask,
  }));
  const removeTask = onRemoveTask ?? storeRemoveTask;

  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { open: openDeleteDialog } = useGlobalModal('deleteDialog');

  const { handleRecreate } = useRecreate();
  const { settings, type, id: taskId } = task;
  const creations = task.creations ?? [];

  const { queueWaitTime, taskState, handleCancelTask, isWaitTimeProlonged } = useTaskState(task, { onTaskUpdate });
  const prevTaskState = useRef(taskState);

  const reportParams = { ...getReportParams(type, settings, task.scene), interaction_position: `${type}_task_card` };
  const hasFinished = FINISHED_STATES.includes(taskState);
  const canCancelTask = taskState === 'queueing';

  // 任务状态变化时的埋点
  useEffect(() => {
    if (prevTaskState.current !== taskState) {
      // 从 queueing 变为 processing 时发送埋点
      if (taskState === 'processing') {
        reportEvent('create_video_loading', {
          ...reportParams,
          is_success: true,
          task_id: taskId,
        });
      }
      // 从 processing 变为 success 时发送埋点
      if (taskState === 'success') {
        reportEvent('create_video_success', {
          ...reportParams,
          is_success: true,
          task_id: taskId,
        });
      }
    }
    prevTaskState.current = taskState;
  }, [taskId, taskState, reportParams, creations.length]);

  // 处理创作操作 - 用于 CreationActionButtons
  const handleSelectedAction = (action: EllipsisActionTypeEnum) => {
    const targetCreation = creations[FIRST_CREATION_INDEX];
    const creationId = targetCreation?.id || '';

    // 删除按钮是任务维度
    if (action === EllipsisActionTypeEnum.Delete) {
      openDeleteDialog({
        creationId: '',
        taskId,
        type: 'task',
        reportParams,
        onDelete: () => {
          removeTask(taskId);
        },
      });
      return;
    }

    // 如果有 creationId，则上报 creation 维度，否则上报 task 维度
    if (action === EllipsisActionTypeEnum.Report) {
      openActionDialog({
        action: EllipsisActionTypeEnum.Report,
        type: creationId ? 'creation' : 'task',
        id: creationId || taskId,
        reportParams,
      });
      return;
    }

    // 其他按钮是生成物维度
    openActionDialog({
      action,
      type: 'creation',
      id: creationId,
      reportParams,
    });
  };

  // 做同款处理
  const handleRecreateAction = () => {
    handleRecreate({
      taskId,
      creationId: creations[FIRST_CREATION_INDEX]?.id || '',
      taskType: type,
      settings,
      input: task.input,
      templateScene: task.scene,
      interactionPosition: `${type}_task_card`,
    });
    if (scrollToTop) scrollToTop();
  };

  return {
    // 任务状态相关
    taskState,
    hasFinished,
    canCancelTask,
    queueWaitTime,
    isWaitTimeProlonged,

    // 事件处理函数
    handleCancelTask,
    handleSelectedAction,
    handleRecreateAction,
  };
};
